import { formatDateTimeWorded } from '@/utils/date';

import { describeAuditTarget, scopeIdText, scopeStateLabel } from './target';
import { type AuditText, auditMessage, joinAuditTexts } from './text';
import type {
    AuditLogEntry,
    AuditLogFilters,
    AuditScopeKind,
    AuditScopeOption,
    AuditScopeState,
} from './types';

/**
 * Where a chip finds the name behind an id. Never the URL: a label carried in a link could
 * be edited to put a false name on an audit screen. Every row matching a scope or actor
 * filter names it, so the first loaded page is usually enough.
 */
export interface AuditChipLabelSources {
    entries: readonly AuditLogEntry[];
    /** Scope picker selections, kept in memory while the page is open. */
    scopes?: readonly AuditScopeOption[];
}

/**
 * 'toolbar' marks a filter the toolbar already shows (time range, person, place, action,
 * outcome): the chip row skips it, and the toolbar can use its label.
 */
export type AuditChipPlacement = 'toolbar' | 'chips';

const PARENT_KIND: Record<AuditScopeKind, AuditScopeKind | null> = {
    client: null,
    location: 'client',
    cage: 'location',
};

function named(name: string, state: AuditScopeState, parent: string | null): AuditText {
    const label = scopeStateLabel(state);
    const withState =
        label === null
            ? name
            : auditMessage('auditLog.chips.value.withState', { name, state: label });
    return parent === null
        ? withState
        : auditMessage('auditLog.chips.value.scopeIn', { name: withState, parent });
}

function optionParent(option: AuditScopeOption): string | null {
    if (option.kind === 'cage') return option.locationName ?? option.clientName;
    return option.kind === 'location' ? option.clientName : null;
}

/** "Site North (Client A)", or "Site #57" when nothing loaded names it. */
export function scopeFilterLabel(
    kind: AuditScopeKind,
    id: number,
    sources: AuditChipLabelSources
): AuditText {
    const parentKind = PARENT_KIND[kind];
    for (const entry of sources.entries) {
        const ref = entry[kind];
        if (ref === null || ref.id !== id || ref.name === null) continue;
        const parent = parentKind === null ? null : (entry[parentKind]?.name ?? null);
        return named(ref.name, ref.state, parent);
    }
    const option = sources.scopes?.find(
        (candidate) => candidate.kind === kind && candidate.id === id && candidate.name !== null
    );
    if (option !== undefined && option.name !== null) {
        return named(option.name, option.state, optionParent(option));
    }
    return scopeIdText(kind, id);
}

/** An actor or impersonator id, named by the email a loaded row carries for it. */
export function userFilterLabel(
    actorType: 'user' | 'api_user',
    id: number,
    sources: AuditChipLabelSources
): AuditText {
    for (const entry of sources.entries) {
        if (
            entry.actorType === actorType &&
            entry.actorUserId === id &&
            entry.actorEmail !== null
        ) {
            return entry.actorEmail;
        }
        if (
            actorType === 'user' &&
            entry.impersonatorUserId === id &&
            entry.impersonatorEmail !== null
        ) {
            return entry.impersonatorEmail;
        }
    }
    return auditMessage(
        actorType === 'api_user' ? 'auditLog.actor.apiUserId' : 'auditLog.actor.userId',
        {
            id: String(id),
        }
    );
}

export function entityTypeText(entityType: string): AuditText {
    return auditMessage(`auditLog.entityType.${entityType}`, undefined, entityType);
}

/** "Pen Pen 3C" from a loaded row, else "Pen #1005". */
export function targetFilterLabel(
    entityType: string,
    entityId: string,
    sources: AuditChipLabelSources
): AuditText {
    const type = entityTypeText(entityType);
    const match = sources.entries.find(
        (entry) => entry.entityType === entityType && entry.entityId === entityId
    );
    const name = match === undefined ? null : describeAuditTarget(match).name;
    return name === null
        ? auditMessage('auditLog.chips.value.typedId', { type, id: entityId })
        : auditMessage('auditLog.chips.value.typedName', { type, name });
}

function isLocalMidnight(date: Date): boolean {
    return (
        date.getHours() === 0 &&
        date.getMinutes() === 0 &&
        date.getSeconds() === 0 &&
        date.getMilliseconds() === 0
    );
}

export function timeValue(from: Date | undefined, to: Date | undefined): AuditText {
    const since = from === undefined ? null : formatDateTimeWorded(from);
    const until = to === undefined ? null : formatDateTimeWorded(to);
    if (since !== null && until !== null) {
        return auditMessage('auditLog.chips.value.between', { from: since, to: until });
    }
    return since !== null
        ? auditMessage('auditLog.chips.value.since', { from: since })
        : auditMessage('auditLog.chips.value.until', { to: until ?? '' });
}

export function translatedList(prefix: string, values: readonly string[]): AuditText {
    return joinAuditTexts(
        values.map((value) => auditMessage(`${prefix}.${value}`, undefined, value))
    );
}

export function timePlacement(f: AuditLogFilters): AuditChipPlacement {
    // The toolbar's picker shows whole days only; a time of day needs a chip to be seen.
    const bounds = [f.from, f.to].filter((bound): bound is Date => bound !== undefined);
    return bounds.every(isLocalMidnight) ? 'toolbar' : 'chips';
}

export function requestDataValue(path: string, value: string | undefined): AuditText {
    return value === undefined
        ? auditMessage('auditLog.chips.value.pathPresent', { path })
        : auditMessage('auditLog.chips.value.pathEquals', { path, value });
}
